import { Capacitor } from '@capacitor/core';

const TOKEN_KEY = 'gchl_auth_token';
const NATIVE_TIMEOUT_MS = 4000;

/**
 * Every native call here is time-boxed. getNativeToken runs inside the axios
 * request interceptor, and axios's own `timeout` does not cover interceptor
 * time — it starts when the request is dispatched. So a bridge call that never
 * answers (seen after the app resumes from the background) used to hang the
 * request before it was ever sent, with no timeout and no error: the caller
 * just spun. localStorage holds the same token, so falling back costs nothing.
 */
function withTimeout(promise, label) {
  let timer;
  return Promise.race([
    Promise.resolve(promise),
    new Promise((_, reject) => {
      timer = setTimeout(() => reject(new Error(`${label} did not answer`)), NATIVE_TIMEOUT_MS);
    }),
  ]).finally(() => clearTimeout(timer));
}

/**
 * Check if running on a native platform (iOS/Android)
 */
export const isNative = () => Capacitor.isNativePlatform();

/**
 * Get the stored auth token.
 * Checks Capacitor Preferences first (native), falls back to localStorage.
 */
export const getNativeToken = async () => {
  // Try Capacitor Preferences first
  if (isNative()) {
    try {
      const { Preferences } = await withTimeout(import('@capacitor/preferences'), 'Preferences plugin load');
      const { value } = await withTimeout(Preferences.get({ key: TOKEN_KEY }), 'Preferences read');
      if (value) return value;
    } catch { /* fall through to localStorage */ }
  }
  // Fallback: localStorage (works in WKWebView even if Capacitor bridge reports web)
  return localStorage.getItem(TOKEN_KEY);
};

/**
 * Store the auth token.
 * Writes to both Capacitor Preferences and localStorage for reliability.
 */
export const setNativeToken = async (token) => {
  // Always store in localStorage as fallback
  localStorage.setItem(TOKEN_KEY, token);
  // Also store in Capacitor Preferences if native
  if (isNative()) {
    try {
      const { Preferences } = await withTimeout(import('@capacitor/preferences'), 'Preferences plugin load');
      await withTimeout(Preferences.set({ key: TOKEN_KEY, value: token }), 'Preferences write');
    } catch { /* localStorage fallback is already set */ }
  }
};

/**
 * Clear the stored auth token from all storage.
 */
export const clearNativeToken = async () => {
  localStorage.removeItem(TOKEN_KEY);
  if (isNative()) {
    try {
      const { Preferences } = await withTimeout(import('@capacitor/preferences'), 'Preferences plugin load');
      await withTimeout(Preferences.remove({ key: TOKEN_KEY }), 'Preferences delete');
    } catch { /* localStorage already cleared */ }
  }
};
